/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useExportClaimStatusData
 */
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import _ from 'lodash';
import { JsonTree } from '@react-awesome-query-builder/antd';

import dateParser from 'lib/dateParser';
import api from 'client/index';
import AppEvents, { Events, Messages } from '@veritable/common/utils/appEvents';
import { downloadBlobFile } from '@veritable/common/utils';
import { MONTH_DATE_YEAR_TIME_FORMAT } from '@veritable/common/constants';
import { parseQueryFilterParams } from 'components/forms/schema/claimsAdvancedFilterSchema';
import { mapClaimStatusLabelToCodes } from 'utils/claims/claimStatusRequests';
import { buildCriteria } from 'utils/advancedFilterUtils';
import { getClaimAdfFieldMap, filterDataKeys } from 'utils/adfUtils';
import { useCustomAttributesStoreValues } from 'store/customAttributes';
import { CUSTOM_ATTRIBUTE_ENTITY_TYPE } from 'app-constants/admin/customAttribute';
/**
 * Mutate to export claim status data
 */

interface IProps {
	searchParam: ClaimStatusRequests.IClaimSearch;
}

const useExportClaimStatusData = (): UseMutationResult<unknown, IAxiosError, IProps, unknown> => {
	const { customAttributes } = useCustomAttributesStoreValues();
	const claimAttributes = customAttributes?.[CUSTOM_ATTRIBUTE_ENTITY_TYPE.claim];
	return useMutation({
		mutationFn: async ({ searchParam }) => {
			const criteria = parseQueryFilterParams({
				query: _.cloneDeep(searchParam?.criteria) as JsonTree,
				field: filterDataKeys.claimStatus,
				parser: mapClaimStatusLabelToCodes,
			});
			const { data } = await api.EzapiApi.exportClaimStatusRequest(
				{
					claimSearch: {
						...searchParam,
						...(buildCriteria(criteria, getClaimAdfFieldMap(claimAttributes)) || {}),
					},
				},
				{ responseType: 'blob' },
			);

			downloadBlobFile(
				data,
				_.kebabCase(`claim-status-data-${dateParser().format(MONTH_DATE_YEAR_TIME_FORMAT)}`),
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'download',
			);
		},
		onSuccess: () => {
			AppEvents.publishApiSuccess('Download complete');
		},
		onMutate: () => {
			AppEvents.publish(Events.Loading, Messages.DownloadingFile);
		},
		onError: (error) => {
			const fileReader = new FileReader();
			fileReader.addEventListener('loadend', () => {
				const parsedError = { response: { data: JSON.parse(fileReader.result as string) } } as IAxiosError;
				AppEvents.publishApiError({ error: parsedError });
			});
			fileReader.readAsText(error.response?.data as unknown as Blob);
		},
	});
};

export default useExportClaimStatusData;
